import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useCreateContract,
  useListCoaches,
  getListContractsQueryKey,
  getListCoachesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/lib/orgContext";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ScrollText, ChevronLeft, ChevronRight, Check, DollarSign, Search, FileText } from "lucide-react";
import { cn, randomId } from "@/lib/utils";
import type { ContractBonus } from "@/components/bonus-repeater";
import { COACH_POSITIONS, COACH_POSITION_VALUES, coachPositionLabel } from "@/lib/coachPositions";

const formSchema = z.object({
  coachId: z.coerce.number().min(1, "Select a coach"),
  contractType: z.enum(COACH_POSITION_VALUES),
  salaryRangeMin: z.coerce.number().min(0).optional(),
  salaryRangeMax: z.coerce.number().min(0).optional(),
  signingBonus: z.coerce.number().min(0).optional(),
  contractLengthOptions: z.string().optional(),
  keyTerms: z.string().optional(),
}).refine((v) => {
  if (v.salaryRangeMin != null && v.salaryRangeMax != null && v.salaryRangeMin > 0 && v.salaryRangeMax > 0) {
    return v.salaryRangeMin <= v.salaryRangeMax;
  }
  return true;
}, { message: "Min salary must be ≤ max salary", path: ["salaryRangeMax"] });

type FormValues = z.infer<typeof formSchema>;

export default function TermSheetNew() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { org } = useOrg();

  const { data: coaches = [], isLoading: coachesLoading } = useListCoaches(undefined, {
    query: { enabled: !!org, queryKey: getListCoachesQueryKey() },
  });

  const createContract = useCreateContract();

  const prefillCoachId = useMemo(() => {
    if (typeof window === "undefined") return 0;
    const p = new URLSearchParams(window.location.search);
    const v = p.get("coachId");
    return v ? Number(v) : 0;
  }, []);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      coachId: prefillCoachId,
      contractType: "head_coach",
      salaryRangeMin: undefined,
      salaryRangeMax: undefined,
      signingBonus: undefined,
      contractLengthOptions: "",
      keyTerms: "",
    },
  });

  const [step, setStep] = useState<1 | 2 | 3>(1);
  // Ref mirror so submit guards never see a stale step after an async advance.
  const stepRef = useRef(step);
  stepRef.current = step;
  const WIZARD_STEPS: { id: 1 | 2 | 3; label: string; fields: Array<keyof FormValues> }[] = [
    { id: 1, label: "Coach & Position", fields: ["coachId", "contractType"] },
    { id: 2, label: "Headline Terms",   fields: ["salaryRangeMin", "salaryRangeMax", "signingBonus", "contractLengthOptions"] },
    { id: 3, label: "Key Terms & Review", fields: ["keyTerms"] },
  ];

  useEffect(() => {
    if (org && org.role !== "admin") {
      toast({ title: "Access denied", description: "Only admins can create term sheets.", variant: "destructive" });
      setLocation("/term-sheets");
    }
  }, [org, setLocation, toast]);

  const goNext = async () => {
    const current = stepRef.current;
    const fields = WIZARD_STEPS[current - 1].fields;
    const ok = fields.length === 0 ? true : await form.trigger(fields);
    if (ok) {
      setStep((s) => (s < 3 ? ((s + 1) as 1 | 2 | 3) : s));
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    } else {
      toast({ title: "Please fix the highlighted fields", variant: "destructive" });
    }
  };

  const goBack = () => {
    setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s));
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const onSubmit = (values: FormValues) => {
    const current = stepRef.current;
    // Final step only. Intermediate Enter/implicit submits must not advance or create —
    // advancement is owned by Next (and Enter keydown → goNext below).
    if (current !== 3) {
      return;
    }

    // documentKind=term_sheet relaxes contract validation; bonuses[] reused for promote flow.
    const bonuses: ContractBonus[] = values.signingBonus && values.signingBonus > 0
      ? [{
          id: randomId(),
          type: "signing",
          label: "Signing Bonus",
          customLabel: "Signing Bonus",
          amount: values.signingBonus,
          condition: "",
          paymentTiming: "one_time_signing",
          isRecurring: false,
        }]
      : [];

    const payload = {
      documentKind: "term_sheet" as const,
      coachId: values.coachId,
      contractType: values.contractType,
      status: "pending" as const,
      salaryRangeMin: values.salaryRangeMin && values.salaryRangeMin > 0 ? values.salaryRangeMin : undefined,
      salaryRangeMax: values.salaryRangeMax && values.salaryRangeMax > 0 ? values.salaryRangeMax : undefined,
      keyTerms: [
        values.contractLengthOptions ? `Length options: ${values.contractLengthOptions}` : "",
        values.keyTerms ?? "",
      ].filter(Boolean).join("\n\n") || undefined,
      // bonuses[] for promote flow; signingBonus for PDF/headline card.
      signingBonus: values.signingBonus && values.signingBonus > 0 ? values.signingBonus : undefined,
      bonuses,
    };

    createContract.mutate({ data: payload as Parameters<typeof createContract.mutate>[0]["data"] }, {
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        toast({ title: "Term sheet created", description: "Submit it for internal approval, then send to the coach." });
        setLocation(`/term-sheets/${(created as { id: number }).id}`);
      },
      onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not create term sheet" }),
    });
  };

  const [coachSearch, setCoachSearch] = useState("");
  const filteredCoaches = coaches.filter((c) => {
    if (!coachSearch) return true;
    const q = coachSearch.toLowerCase();
    const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
    return (
      fullName.includes(q) ||
      (c.sport ?? "").toLowerCase().includes(q) ||
      (c.title ?? "").toLowerCase().includes(q)
    );
  });

  const selectedCoachId = form.watch("coachId");
  const selectedCoach = coaches.find((c) => c.id === selectedCoachId);

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/term-sheets"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-purple-600" />
            New Term Sheet
          </h1>
          <p className="text-muted-foreground mt-1">
            Capture headline terms for an early-stage offer. Term sheets are non-binding and can be promoted to a full contract once accepted.
          </p>
        </div>
      </div>

      <Form {...form}>
        <form
          // Never create via native/implicit submit — Create is type="button" and
          // calls handleSubmit explicitly. This blocks Enter-key races on step 3.
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className="space-y-6"
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            const t = e.target as HTMLElement | null;
            // Only text-like inputs — never hijack buttons, selects, radios, etc.
            if (!t || t.tagName !== "INPUT") return;
            const inputType = ((t as HTMLInputElement).type || "text").toLowerCase();
            if (
              inputType === "button" ||
              inputType === "submit" ||
              inputType === "reset" ||
              inputType === "checkbox" ||
              inputType === "radio" ||
              inputType === "file" ||
              inputType === "image"
            ) {
              return;
            }
            // Block implicit form submit. On steps 1–2, advance via goNext so a
            // second Enter cannot race into create. On step 3, swallow only.
            e.preventDefault();
            if (stepRef.current < 3) {
              void goNext();
            }
          }}
        >
          {/* ── Wizard stepper — mirrors contracts-new for parity ───── */}
          <ol
            className="flex items-stretch gap-1 sm:gap-2 rounded-xl border bg-card px-3 py-3 sm:px-4"
            data-testid="term-sheet-wizard-stepper"
            aria-label="Term sheet creation steps"
          >
            {WIZARD_STEPS.map((s) => {
              const isCompleted = s.id < step;
              const isCurrent = s.id === step;
              return (
                <li key={s.id} className="flex-1 min-w-[110px]">
                  <button
                    type="button"
                    onClick={async () => {
                      if (s.id < step) { setStep(s.id); return; }
                      if (s.id === step) return;
                      const fieldsToValidate = WIZARD_STEPS
                        .filter((x) => x.id >= step && x.id < s.id)
                        .flatMap((x) => x.fields);
                      const ok = fieldsToValidate.length === 0
                        ? true
                        : await form.trigger(fieldsToValidate);
                      if (ok) setStep(s.id);
                      else toast({ title: "Finish the current step first", description: "Some required fields still need attention.", variant: "destructive" });
                    }}
                    className={cn(
                      "group w-full text-left rounded-lg px-2.5 py-2 transition-colors",
                      "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    )}
                    data-testid={`wizard-step-${s.id}`}
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold border",
                          isCompleted && "bg-purple-600 text-white border-purple-600",
                          isCurrent && "bg-primary text-primary-foreground border-primary",
                          !isCompleted && !isCurrent && "bg-muted text-muted-foreground border-border",
                        )}
                      >
                        {isCompleted ? <Check className="h-3.5 w-3.5" /> : s.id}
                      </span>
                      <span className={cn("text-xs sm:text-sm font-medium truncate", !isCurrent && !isCompleted && "text-muted-foreground")}>
                        {s.label}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "mt-2 h-1 rounded-full",
                        isCompleted && "bg-purple-600",
                        isCurrent && "bg-primary",
                        !isCompleted && !isCurrent && "bg-muted",
                      )}
                    />
                  </button>
                </li>
              );
            })}
          </ol>

          {step === 1 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Coach</CardTitle>
                  <CardDescription>Pick the coach this term sheet is for.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search coaches…"
                      value={coachSearch}
                      onChange={(e) => setCoachSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="coachId"
                    render={({ field }) => (
                      <FormItem>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto rounded-md border p-2">
                          {coachesLoading && <div className="text-sm text-muted-foreground p-3">Loading coaches…</div>}
                          {!coachesLoading && filteredCoaches.length === 0 && (
                            <div className="text-sm text-muted-foreground p-3">No coaches match your search.</div>
                          )}
                          {filteredCoaches.map((c) => {
                            const isSelected = field.value === c.id;
                            const fullName = `${c.firstName} ${c.lastName}`.trim();
                            return (
                              <button
                                key={c.id}
                                type="button"
                                data-testid={`button-pick-coach-${c.id}`}
                                onClick={() => field.onChange(c.id)}
                                className={`text-left rounded-md border px-3 py-2 transition-colors ${isSelected ? "border-purple-600 bg-purple-50" : "border-border hover:bg-muted/40"}`}
                              >
                                <div className="text-sm font-medium truncate">{fullName}</div>
                                <div className="text-xs text-muted-foreground truncate">{c.sport ?? "—"} · {c.title ?? "—"}</div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedCoach && (
                          <FormDescription>
                            Selected: <strong>{`${selectedCoach.firstName} ${selectedCoach.lastName}`.trim()}</strong>
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Position</CardTitle>
                  <CardDescription>Which role this term sheet is for.</CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="contractType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Position *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-term-sheet-position"><SelectValue /></SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COACH_POSITIONS.map((p) => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </>
          )}

          {step === 2 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    Salary Range
                  </CardTitle>
                  <CardDescription>The minimum a coach's agent needs to evaluate the offer.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="salaryRangeMin"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Salary Range — Min ($)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                              <Input type="number" className="pl-7" placeholder="e.g. 250000" {...field} value={field.value ?? ""} data-testid="input-salary-min" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="salaryRangeMax"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Salary Range — Max ($)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                              <Input type="number" className="pl-7" placeholder="e.g. 350000" {...field} value={field.value ?? ""} data-testid="input-salary-max" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <p className="text-[0.8rem] text-muted-foreground">
                    Leave both empty if salary is still TBD. The PDF will display "TBD" instead of a range.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    Signing Bonus &amp; Length
                  </CardTitle>
                  <CardDescription>Optional sweeteners — leave blank if not applicable.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="signingBonus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Signing Bonus ($)</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                            <Input type="number" className="pl-7" placeholder="e.g. 100000" {...field} value={field.value ?? ""} data-testid="input-signing-bonus" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contractLengthOptions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contract Length Options</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. 3 years, 5 years, or rolling option" {...field} data-testid="input-length-options" />
                        </FormControl>
                        <FormDescription>Free-form — list ranges or alternatives the agent can pick from.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </>
          )}

          {step === 3 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Key Terms</CardTitle>
                  <CardDescription>Bullet the deal points (incentives, buyout sketch, NIL, perks). The agent will redline these.</CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="keyTerms"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            rows={8}
                            placeholder={`• 4-year guaranteed term with mutual option for 5th year
• Performance bonuses for conference championship and bowl appearance
• 12% reciprocal buyout, declining 25% per year
• Standard NIL/title-IX/governing-law clauses to be added in full contract`}
                            {...field}
                            data-testid="textarea-key-terms"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* ── Review summary — mirrors the review pattern in
                  contracts-new so admins can confirm before creating. */}
              <Card data-testid="term-sheet-review-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Review &amp; Create
                  </CardTitle>
                  <CardDescription>Confirm the offer summary below, then create the term sheet.</CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-y-2 gap-x-4 text-sm">
                    <dt className="text-muted-foreground">Coach</dt>
                    <dd className="font-medium" data-testid="review-coach">
                      {selectedCoach
                        ? `${selectedCoach.firstName} ${selectedCoach.lastName}`.trim()
                        : <span className="italic text-muted-foreground">Not selected</span>}
                    </dd>
                    <dt className="text-muted-foreground">Position</dt>
                    <dd data-testid="review-position">
                      {coachPositionLabel(form.getValues("contractType"))}
                    </dd>
                    <dt className="text-muted-foreground">Salary range</dt>
                    <dd data-testid="review-salary">
                      {(() => {
                        const lo = form.getValues("salaryRangeMin");
                        const hi = form.getValues("salaryRangeMax");
                        if (lo && hi) return <><span className="font-medium">${lo.toLocaleString()}</span> – <span className="font-medium">${hi.toLocaleString()}</span></>;
                        if (hi) return <>Up to <span className="font-medium">${hi.toLocaleString()}</span></>;
                        if (lo) return <>From <span className="font-medium">${lo.toLocaleString()}</span></>;
                        return <span className="italic text-muted-foreground">TBD</span>;
                      })()}
                    </dd>
                    <dt className="text-muted-foreground">Signing bonus</dt>
                    <dd data-testid="review-signing-bonus">
                      {(() => {
                        const v = form.getValues("signingBonus");
                        return v && v > 0
                          ? <span className="font-medium">${v.toLocaleString()}</span>
                          : <span className="italic text-muted-foreground">None</span>;
                      })()}
                    </dd>
                    <dt className="text-muted-foreground">Length options</dt>
                    <dd data-testid="review-length">
                      {form.getValues("contractLengthOptions") || <span className="italic text-muted-foreground">None</span>}
                    </dd>
                    <dt className="text-muted-foreground">Key terms</dt>
                    <dd className="text-xs whitespace-pre-wrap" data-testid="review-key-terms">
                      {form.getValues("keyTerms") || <span className="italic text-muted-foreground text-sm">None recorded</span>}
                    </dd>
                  </dl>
                  <div className="mt-4 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900">
                    Term sheets are non-binding. After creating, submit it for internal approval, then send to the coach. Once accepted, you can promote it to a full contract.
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <div className="flex justify-between gap-2">
            <Button type="button" variant="outline" onClick={goBack} disabled={step === 1} data-testid="button-back">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < 3 ? (
              <Button type="button" onClick={() => void goNext()} data-testid="button-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                disabled={createContract.isPending}
                onClick={() => void form.handleSubmit(onSubmit)()}
                data-testid="button-create-term-sheet"
              >
                {createContract.isPending ? "Creating…" : "Create Term Sheet"}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
